/** @file Parses exported rig JSON and its atlas into immutable SkeletonData; the only loader. */
import type { AtlasJson, Pose2DJson } from '@pose2d/format/types';
import type { SkeletonData } from '../skeletonData';
import { checkRigHeader } from '../_internal/load/checkRigHeader';
import { createInterner } from '../_internal/load/createInterner';
import { indexIn } from '../_internal/load/indexIn';
import { nameIndex } from '../_internal/load/nameIndex';
import { readBones } from '../_internal/load/readBones';
import { readDirections } from '../_internal/load/readDirections';
import { readPages } from '../_internal/load/readPages';
import { readAttachments } from '../_internal/load/readAttachments';
import { readAnimations } from '../_internal/load/readAnimations';
import { readRigConstraints } from '../_internal/load/readRigConstraints';
import { readSkins } from '../_internal/load/readSkins';
import { readSlots } from '../_internal/load/readSlots';

/**
 * Builds SkeletonData from an export document and its atlas (page UVs via regionUv). Checks only `format` and the
 * major version (throws on a mismatch) and reads fields defensively: unknown fields from a newer minor are ignored
 * (§5.5). SkeletonJsonSchema is the exporter's/test's strict contract, not a runtime gate (core has no zod).
 * Indices, codes, colours, IK bone counts (1-2), update-op kinds, the iso tile size (finite, positive), deform key
 * ranges and draw-order permutations are still checked (VALIDATION with the JSON path). Placeholders are interned in
 * first-seen order (slot setups, skins, attachment keys); attachment handles are stored in slot-bone space (y-up).
 */
export function loadSkeleton(json: Pose2DJson, atlas: AtlasJson): SkeletonData {
  checkRigHeader(json);
  const placeholders = createInterner();
  const bones = readBones(json.bones);
  const slots = readSlots(json.slots, bones.length, placeholders);
  const { ik, sockets, updateOrder } = readRigConstraints(json, bones.length);
  const skins = readSkins(json.skins, slots.length, json.attachments.length, placeholders);
  const attachments = readAttachments(json.attachments, atlas, bones.length);
  const data: SkeletonData = {
    version: json.version,
    hash: json.hash,
    bones,
    boneIndex: nameIndex(bones),
    updateOrder,
    slots,
    slotIndex: nameIndex(slots),
    placeholders: placeholders.names,
    placeholderIndex: placeholders.index,
    ik,
    sockets,
    attachments,
    attachmentIndex: nameIndex(attachments),
    skins,
    skinIndex: nameIndex(skins),
    defaultSkin: indexIn(json.defaultSkin, skins.length, 'defaultSkin'),
    events: json.events.map((e) => ({ name: e.name, int: e.int, float: e.float, string: e.string })),
    mixDefaults: new Map((json.mixDefaults ?? []).map(([from, to, seconds]): [string, number] => [`${from}>${to}`, seconds])),
    directions: readDirections(json.directions, { slots: slots.length, skins: skins.length, animations: json.animations.length }),
    animations: [],
    animationIndex: new Map(),
    pages: readPages(atlas),
  };
  data.animations = readAnimations(json.animations, data, placeholders);
  data.animationIndex = nameIndex(data.animations);
  return data;
}
